import React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import { GOOGLE_CAPTCHA_TOUR_KEY } from "../../constants";

/**
 * Guest details step. Name, email and phone are all required - they are the
 * gym's only record of who is coming, since a guest has no MSB account.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function validateGuestForm(values, t) {
  const errors = {};
  if (values.name.trim().length < 2) errors.name = t("form.errors.name");
  if (!EMAIL_PATTERN.test(values.email.trim())) {
    errors.email = t("form.errors.email");
  }
  const digits = values.phone.replace(/\D/g, "");
  if (digits.length < 7 || digits.length > 15) {
    errors.phone = t("form.errors.phone");
  }
  return errors;
}

const BookTourForm = ({
  values,
  onValuesChange,
  onSubmit,
  onBack,
  submitting,
  submitError,
}) => {
  const { t } = useTranslation("book-tour");
  const [errors, setErrors] = React.useState({});
  const [captchaToken, setCaptchaToken] = React.useState(null);
  const [captchaError, setCaptchaError] = React.useState(null);
  const captchaRef = React.useRef(null);

  const set = (key) => (e) => {
    const v = e.target.value;
    onValuesChange({ ...values, [key]: v });
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validateGuestForm(values, t);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    if (!captchaToken) {
      setCaptchaError(t("form.errors.captcha"));
      return;
    }
    setCaptchaError(null);
    const ok = await onSubmit({
      name: values.name.trim(),
      email: values.email.trim(),
      phone: values.phone.trim(),
      captcha_token: captchaToken,
    });
    // A reCAPTCHA token is single-use: a rejected submit must start a fresh
    // challenge or every retry fails with "already verified".
    if (!ok) {
      captchaRef.current?.reset();
      setCaptchaToken(null);
    }
  };

  return (
    <form className="book-tour-form" onSubmit={handleSubmit} noValidate>
      <div className="book-tour-field">
        <label htmlFor="book-tour-name">{t("form.name")}</label>
        <input
          id="book-tour-name"
          type="text"
          autoComplete="name"
          value={values.name}
          onChange={set("name")}
          aria-invalid={Boolean(errors.name)}
          aria-describedby={errors.name ? "book-tour-name-error" : undefined}
        />
        {errors.name && (
          <p className="book-tour-field-error" id="book-tour-name-error">
            {errors.name}
          </p>
        )}
      </div>

      <div className="book-tour-field">
        <label htmlFor="book-tour-email">{t("form.email")}</label>
        <input
          id="book-tour-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={values.email}
          onChange={set("email")}
          aria-invalid={Boolean(errors.email)}
          aria-describedby={errors.email ? "book-tour-email-error" : undefined}
        />
        {errors.email && (
          <p className="book-tour-field-error" id="book-tour-email-error">
            {errors.email}
          </p>
        )}
      </div>

      <div className="book-tour-field">
        <label htmlFor="book-tour-phone">{t("form.phone")}</label>
        <input
          id="book-tour-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={values.phone}
          onChange={set("phone")}
          aria-invalid={Boolean(errors.phone)}
          aria-describedby={errors.phone ? "book-tour-phone-error" : undefined}
        />
        {errors.phone && (
          <p className="book-tour-field-error" id="book-tour-phone-error">
            {errors.phone}
          </p>
        )}
      </div>

      <div className="book-tour-captcha">
        <ReCAPTCHA
          ref={captchaRef}
          sitekey={GOOGLE_CAPTCHA_TOUR_KEY}
          onChange={(token) => {
            setCaptchaToken(token);
            if (token) setCaptchaError(null);
          }}
          onExpired={() => setCaptchaToken(null)}
        />
        {captchaError && (
          <p className="book-tour-field-error" role="alert">
            {captchaError}
          </p>
        )}
      </div>

      {submitError && (
        <p className="book-tour-error" role="alert">
          {submitError}
        </p>
      )}

      <div className="book-tour-actions">
        <button
          type="button"
          className="book-tour-btn-secondary"
          onClick={onBack}
        >
          {t("form.back")}
        </button>
        <button
          type="submit"
          className="book-tour-btn-primary"
          disabled={submitting}
        >
          {submitting ? t("form.submitting") : t("form.submit")}
        </button>
      </div>
    </form>
  );
};

export default BookTourForm;
